import { createApp } from 'vue';
import router from '@/router';
import { createPinia } from 'pinia';
import Vue3Toastify, { type ToastContainerOptions, toast } from 'vue3-toastify';
import vuetify from '@/plugins/vuetify';
import DelayedToastPlugin from '@/plugins/toastDelayedPlugin';
import { i18n } from '@/plugins/i18n';
import App from '@/App.vue';

import '@mdi/font/css/materialdesignicons.css';
import 'vue3-toastify/dist/index.css';

/**
 * Rejestracja Service Worker'a (PWA).
 *
 * Kluczowe zachowania:
 *  • SW żyje na ścieżce '/service-worker.js' (public folder serwowany na root)
 *  • update-detection: nasłuchujemy `updatefound` na registration → gdy
 *    nowy SW przejdzie do `installed` (i jest waiting), pokazujemy toast
 *    z linkiem „Kliknij aby zaktualizować". Akcja wysyła SKIP_WAITING do
 *    waiting SW → ten się aktywuje → `controllerchange` event → reload.
 *  • dla legacy userów którzy mieli zarejestrowany stary SW (Workbox
 *    z testy-master): pierwsza wizyta po deploy wciąż używa starego SW,
 *    ale browser zauważa nowy SW na drugiej wizycie i triggeruje update.
 *    Dla pewności na każdym window.load wywołujemy registration.update()
 *    — wymusza sprawdzenie czy jest nowsza wersja.
 *  • dev (Vite): NIE rejestrujemy SW żeby HMR działał poprawnie. Zmienna
 *    `import.meta.env.PROD` rozróżnia.
 */
const registerServiceWorker = (): void => {
	if (!('serviceWorker' in navigator)) return;
	if (!import.meta.env.PROD) return;
	if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') return;

	window.addEventListener('load', async () => {
		try {
			const registration = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });

			// Periodic check for updates — co kilka minut sprawdza czy doszedł
			// nowy build (np. user trzyma kartę otwartą cały dzień).
			setInterval(() => {
				registration.update().catch(() => { /* ignore */ });
			}, 60 * 60 * 1000); // co godzinę

			registration.addEventListener('updatefound', () => {
				const newWorker = registration.installing;
				if (!newWorker) return;
				newWorker.addEventListener('statechange', () => {
					if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
						// Nowa wersja zainstalowana, waiting na activate
						toast.info('Dostępna nowa wersja aplikacji. Kliknij aby zaktualizować.', {
							autoClose: false,
							closeOnClick: true,
							onClick: () => {
								newWorker.postMessage({ type: 'SKIP_WAITING' });
							},
						});
					}
				});
			});

			let refreshing = false;
			navigator.serviceWorker.addEventListener('controllerchange', () => {
				if (refreshing) return;
				refreshing = true;
				window.location.reload();
			});
		} catch (e) {
			// Ignoruj — bez SW aplikacja działa, tylko bez offline support
		}
	});
};
registerServiceWorker();

const app = createApp(App);
app.use(router);
app.use(createPinia());
app.use(vuetify);
app.use(i18n);
app.use(Vue3Toastify, {
	autoClose: 3000,
	position: 'top-right',
} as ToastContainerOptions);
app.use(DelayedToastPlugin);

const beforeAppMountActions = async () => {
	const locale: string = 'pl';
	
	window.t = i18n.global.t;
	
	// @ts-ignore
	i18n.global.locale.value = locale;
};

beforeAppMountActions().then(() => {
	app.mount('#app');
});
